/* Page Layout Reusable Components */

import React from 'react';
import { Link } from 'react-router-dom';

// Section Container Component
export function Section({ children, className = '', ...props }) {
  return (
    <section className={`section ${className}`} {...props}>
      {children}
    </section>
  );
}

// Hero Section with Image and Overlay
export function Hero({ title, subtitle, imageSrc, imageAlt, children, imagePosition = 'center' }) {
  return (
    <div className="hero">
      {imageSrc && (
        <div className="hero-image-container">
          <img 
            src={imageSrc} 
            alt={imageAlt || title}
            className="hero-image"
            loading="eager"
            style={{ objectPosition: imagePosition }}
          />
          <div className="hero-overlay"></div>
        </div>
      )}
      {(title || subtitle || children) && (
        <div className="hero-content">
          {title && <h1 className="hero-title">{title}</h1>}
          {subtitle && <p className="hero-subtitle">{subtitle}</p>}
          {children && <div className="hero-actions">{children}</div>}
        </div>
      )}
    </div>
  );
}

// Page Header Component
export function PageHeader({ title, subtitle }) {
  return (
    <header className="page-header">
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </header>
  );
}

// Card Component
export function Card({ children, className = '', highlighted = false }) {
  return (
    <div className={`card ${highlighted ? 'card-highlighted' : ''} ${className}`}>
      {children}
    </div>
  );
}

// Three Column Grid Component
export function ThreeColumnGrid({ children }) {
  return (
    <div className="row">
      {React.Children.map(children, (child, index) => (
        <div key={index} className="col-4 large-4 columns">
          {child}
        </div>
      ))}
    </div>
  );
}

// Two Column Grid Component
export function TwoColumnGrid({ children, reversed = false }) {
  const items = React.Children.toArray(children);
  return (
    <div className={`row two-column ${reversed ? 'reversed' : ''}`}>
      <div className="col-6 large-6 columns">
        {items[0]}
      </div>
      <div className="col-6 large-6 columns">
        {items[1]}
      </div>
    </div>
  );
}

// Call to Action Component — uses <Link> for internal routes
export function CallToAction({ title, description, buttonText, buttonHref, external = false }) {
  const ButtonContent = external ? (
    <a href={buttonHref} className="button button-primary" target="_blank" rel="noopener noreferrer">
      {buttonText}
    </a>
  ) : (
    <Link to={buttonHref} className="button button-primary">
      {buttonText}
    </Link>
  );

  return (
    <div className="panel panel-highlight">
      <div className="row" style={{ alignItems: 'center' }}>
        <div className="col-9 large-9 columns">
          {title && <h3>{title}</h3>}
          {description && <p>{description}</p>}
        </div>
        <div className="col-3 large-3 columns text-right">
          {ButtonContent}
        </div>
      </div>
    </div>
  );
}

// Feature Card Component
export function FeatureCard({ icon, title, description }) {
  return (
    <div className="feature-card text-center">
      {icon && <div className="feature-icon">{icon}</div>}
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
  );
}

// Info Panel Component
export function InfoPanel({ title, children }) {
  return (
    <div className="panel">
      {title && <h3>{title}</h3>}
      {children}
    </div>
  );
}
